import { Card } from '@ics-portal/guardian';
import {
    GraphQLTableAdapter,
    IcsDataTable,
    type TableApi,
    TablePadding,
} from '@ics-portal/ics-data-table';
import { forwardRef, useMemo, type ReactNode } from 'react';
import type { Component, GetComponentsPagedQuery } from '@gql/graphql';
import apolloClient from '@api/client';
import { GET_COMPONENTS_PAGED } from '@api/queries/components-paged';
import { type Maybe } from '@models/Maybe';
import { useCardVisibility } from '@hooks/useCardVisibility';
import {
    COMPONENT_COLUMNS,
    type ComponentRow,
} from '@pages/notification-components/components/ComponentsCard/columns';

function toComponent(row: ComponentRow): Component {
    return {
        id: row.id,
        name: row.name,
        version: row.version,
        eolReached: row.eolReached,
        isValid: row.isValid,
        componentVendor: {
            id: row.componentVendor.id,
            name: row.componentVendor.name,
            ifa: row.componentVendor.ifa,
            description: row.componentVendor.description,
        },
        eolDate: row.eolDate,
        monitoredSince: row.monitoredSince,
        cpeName: row.cpeName,
        url: row.url,
        securityUrl: row.securityUrl,
        createTime: row.createTime,
        updateTime: row.updateTime,
    };
}

interface ComponentsCardProps {
    selectedChange: (component: Maybe<Component>) => void;
}

export const ComponentsCard = forwardRef<
    TableApi<ComponentRow>,
    ComponentsCardProps
>(function ComponentsCard({ selectedChange }, ref): ReactNode {
    const cardVisibility = useCardVisibility('notification-components');

    const adapter = useMemo(
        () =>
            new GraphQLTableAdapter<GetComponentsPagedQuery, ComponentRow>({
                client: apolloClient,
                query: GET_COMPONENTS_PAGED,
                extractConnection: (d) =>
                    d.components ?? { totalCount: 0, edges: [] },
                searchFieldPaths: [
                    'name',
                    'version',
                    'hasComponentVendorWith.name',
                ],
            }),
        [],
    );

    return (
        <Card title="Notification components" {...cardVisibility}>
            <IcsDataTable<ComponentRow>
                ref={ref}
                id="notification-components"
                description="List of all components"
                adapter={adapter}
                columns={COMPONENT_COLUMNS}
                selection="single"
                onSelectionChange={(rows) => {
                    selectedChange(rows[0] ? toComponent(rows[0]) : null);
                }}
                padding={TablePadding.NONE}
                emptyState={{
                    title: 'No components',
                    description: 'There are no notification components yet.',
                    noResultsTitle: 'No components match your search.',
                }}
            />
        </Card>
    );
});
